import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Alert, Button, Card, CardBody, Input, Spinner, Table } from 'reactstrap'
import TitlePage from 'components/atoms/title-page'
import InboundJourneyApiV2 from 'api/v2/admins/inbound-journey-api-v2'
import { extractPhase3Meta, formatPhase3Date, getPhase3ErrorMessage, readPhase3Text } from '../helpers/phase3-inbound-journey-helper'

const Phase3ConsignmentListPage = ({ pageUtils, apiNamespace = 'all_team' }) => {
  const [ consignments, setConsignments ] = useState([])
  const [ meta, setMeta ] = useState({ current_page: 1, total_pages: 1, total_count: 0 })
  const [ q, setQ ] = useState('')
  const [ searchText, setSearchText ] = useState('')
  const [ isLoading, setIsLoading ] = useState(false)

  const fetchConsignments = useCallback(async ({ page = 1, keyword = q } = {}) => {
    setIsLoading(true)

    try {
      const response = await InboundJourneyApiV2.getConsignments({ q: keyword, page, per_page: 20, apiNamespace })
      setConsignments(response.data?.data || [])
      setMeta(extractPhase3Meta(response))
    } catch (error) {
      pageUtils?.setApiErrorMsg?.(error.response?.data || getPhase3ErrorMessage(error))
    } finally {
      setIsLoading(false)
    }
  }, [ apiNamespace, pageUtils, q ])

  useEffect(() => {
    fetchConsignments({ page: 1, keyword: '' })
  }, [ fetchConsignments ])

  const submitSearch = () => {
    setQ(searchText)
    fetchConsignments({ page: 1, keyword: searchText })
  }

  const resetSearch = () => {
    setSearchText('')
    setQ('')
    fetchConsignments({ page: 1, keyword: '' })
  }

  const renderConsignor = (consignment) => {
    const consignor = consignment.consignor || {}
    const label = [ readPhase3Text(consignor.name) || readPhase3Text(consignor.email), consignor.id ? `#${consignor.id}` : '' ].filter(Boolean).join(' ')

    if (!consignor.id) return label || '-'

    return <Link to={ `/app/sales/users/${consignor.id}` }>{ label }</Link>
  }

  const renderBatch = (consignment) => {
    const batchCode = consignment.inventory_batch_code || consignment.inventory_batch?.batch_code
    if (!batchCode) return '-'

    return <Link to={ `/app/all-team/inbound-journey/inventory-batches?q=${encodeURIComponent(batchCode)}` }>{ batchCode }</Link>
  }

  return (
    <>
      <TitlePage mainTitle='All Team' subTitle='Consignments' />

      <Card className='mb-4'>
        <CardBody>
          <Alert color='info'>
            { 'Data consignment dari dashboard lama dapat dilihat di New Dashboard. Edit consignor hanya untuk owner/admin atau admin yang membuat consignment tersebut.' }
          </Alert>
          <div className='d-flex' style={ { gap: 8 } }>
            <Input
              value={ searchText }
              placeholder='Cari SKU, produk, consignor, email, atau kode batch'
              onChange={ (event) => setSearchText(event.target.value) }
              onKeyDown={ (event) => { if (event.key === 'Enter') submitSearch() } }
            />
            <Button color='primary' onClick={ submitSearch }>Cari</Button>
            <Button color='secondary' outline onClick={ resetSearch }>Reset</Button>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          { isLoading ? (
            <div className='d-flex align-items-center'>
              <Spinner size='sm' className='mr-2' />
              { 'Memuat consignment...' }
            </div>
          ) : (
            <div className='table-responsive'>
              <Table bordered hover responsive>
                <thead>
                  <tr>
                    <th>Consignment</th>
                    <th>SKU</th>
                    <th>Product</th>
                    <th>Consignor Name</th>
                    <th>Consignor Detail</th>
                    <th>Internal Cat</th>
                    <th>Type</th>
                    <th>Method</th>
                    <th>IQ/SQ</th>
                    <th>Batch</th>
                    <th>State</th>
                    <th>Created By</th>
                    <th>Updated</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  { consignments.length === 0 ? (
                    <tr>
                      <td colSpan='14' className='text-center text-muted'>Consignment tidak ditemukan.</td>
                    </tr>
                  ) : consignments.map((consignment) => (
                    <tr key={ consignment.id }>
                      <td>#{ consignment.id }</td>
                      <td className='font-weight-bold'>{ consignment.product?.sku || '-' }</td>
                      <td>{ consignment.product?.name || '-' }</td>
                      <td>{ renderConsignor(consignment) }</td>
                      <td>{ consignment.consignor_detail?.formatted_name || consignment.consignor_detail?.name || '-' }</td>
                      <td>{ consignment.product?.internal_category || '-' }</td>
                      <td>{ consignment.consignment_type || '-' }</td>
                      <td>{ consignment.consignment_method || '-' }</td>
                      <td>{ consignment.inventory_quantity || 0 }/{ consignment.sellable_quantity || 0 }</td>
                      <td>{ renderBatch(consignment) }</td>
                      <td>{ consignment.state || '-' }</td>
                      <td>{ consignment.created_by?.email || '-' }</td>
                      <td>{ formatPhase3Date(consignment.updated_at) }</td>
                      <td>
                        <Button tag={ Link } to={ `/app/all-team/consignments/${consignment.id}/edit` } color='primary' size='sm' outline>
                          { consignment.can_edit_consignor ? 'Edit' : 'Lihat' }
                        </Button>
                      </td>
                    </tr>
                  )) }
                </tbody>
              </Table>
            </div>
          ) }

          <div className='d-flex justify-content-between align-items-center mt-3'>
            <div className='text-muted'>
              { `Halaman ${meta.current_page || 1} dari ${meta.total_pages || 1} — ${meta.total_count || 0} consignment` }
            </div>
            <div>
              <Button color='secondary' outline disabled={ (meta.current_page || 1) <= 1 || isLoading } onClick={ () => fetchConsignments({ page: (meta.current_page || 1) - 1 }) }>
                Sebelumnya
              </Button>
              <Button color='secondary' outline className='ml-2' disabled={ (meta.current_page || 1) >= (meta.total_pages || 1) || isLoading } onClick={ () => fetchConsignments({ page: (meta.current_page || 1) + 1 }) }>
                Berikutnya
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>
    </>
  )
}

export default Phase3ConsignmentListPage
